/**
 * BuyForFamily - One-Minute Buy Flow Component
 * 
 * A streamlined 4-step purchase flow:
 * STEP 1: WHO - Select beneficiary
 * STEP 2: WHERE - Select merchant  
 * STEP 3: HOW MUCH - Select amount
 * STEP 4: REVIEW - Confirm purchase
 * 
 * Designed for returning customers to complete in ~1 minute
 * 
 * BUY AGAIN: Pass buyAgainData prop to pre-fill:
 * - beneficiary (from recent authorization)
 * - merchant (from recent authorization)
 * - amount (optional, from last purchase)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, ArrowRight, Check, Users, Store, DollarSign,
  Loader2, MapPin, MessageCircle, Phone, ChevronRight,
  Plus, Star, Clock, AlertCircle, X, CheckCircle2, Bell, Copy,
  Repeat
} from 'lucide-react';
import QRCode from 'react-qr-code';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Dialog,
  DialogContent,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { API_URL } from '../utils/apiConfig';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { formatUSD, formatDOP, quickAmounts } from '../styles/designSystem';

/**
 * Auto-format phone number to international format
 * Accepts any format and converts to +1 XXX XXX XXXX for US/DR numbers
 */
const formatPhoneNumber = (value) => {
  // Get only digits from input
  const digits = value.replace(/\D/g, '');
  
  // Handle empty input
  if (digits.length === 0) return '';
  
  // For 10 digits (standard US/DR without country code)
  if (digits.length <= 10) {
    if (digits.length <= 3) {
      return `+1 ${digits}`;
    } else if (digits.length <= 6) {
      return `+1 ${digits.slice(0, 3)} ${digits.slice(3)}`;
    } else {
      return `+1 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
    }
  }
  
  // For 11+ digits (includes country code)
  if (digits.startsWith('1') && digits.length === 11) {
    // US/DR with country code 1
    return `+1 ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 11)}`;
  }
  
  // Generic international format for other numbers
  if (digits.length <= 12) {
    return `+${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`.trim();
  }
  
  // Cap at reasonable length
  return `+${digits.slice(0, 15)}`;
};

/**
 * Normalize phone number for API submission
 */
const normalizePhoneNumber = (value) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }
  return `+${digits}`;
};

// Quick amount buttons
const QUICK_AMOUNTS = [25, 50, 75, 100, 150, 200];

// Step indicator
function StepIndicator({ currentStep, totalSteps = 4 }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-6" data-testid="buy-flow-step-indicator">
      {[...Array(totalSteps)].map((_, i) => (
        <div 
          key={i}
          data-testid={`buy-flow-step-${i + 1}`}
          className={`h-2 rounded-full transition-all duration-300 ${
            i < currentStep 
              ? 'w-8 bg-purple-600' 
              : i === currentStep 
                ? 'w-8 bg-purple-400'
                : 'w-2 bg-slate-200'
          }`}
        />
      ))}
    </div>
  );
}

// Beneficiary card
function BeneficiaryCard({ beneficiary, isSelected, onSelect, isRecent }) {
  return (
    <button
      onClick={() => onSelect(beneficiary)}
      className={`w-full p-4 rounded-2xl border-2 transition-all text-left ${
        isSelected 
          ? 'border-purple-500 bg-purple-50' 
          : 'border-slate-200 hover:border-purple-200 hover:bg-slate-50'
      }`}
      data-testid={`beneficiary-${beneficiary.id}`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${
          isSelected ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-600'
        }`}>
          {beneficiary.name?.charAt(0)?.toUpperCase() || 'B'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-semibold text-slate-900 truncate">{beneficiary.name}</p>
            {isRecent && (
              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                Recent
              </span>
            )}
          </div>
          {beneficiary.remaining_dop > 0 && (
            <p className="text-sm text-emerald-600 font-medium">
              RD${beneficiary.remaining_dop?.toLocaleString()} disponible
            </p>
          )}
        </div>
        {isSelected && (
          <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
        )}
      </div>
    </button>
  );
}

// Merchant card
function MerchantCard({ merchant, isSelected, onSelect, isPreferred }) {
  return (
    <button
      onClick={() => onSelect(merchant)}
      className={`w-full p-4 rounded-2xl border-2 transition-all text-left ${
        isSelected 
          ? 'border-purple-500 bg-purple-50' 
          : 'border-slate-200 hover:border-purple-200 hover:bg-slate-50'
      }`}
      data-testid={`merchant-${merchant.id}`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
          isSelected ? 'bg-purple-600 text-white' : 'bg-emerald-100 text-emerald-600'
        }`}>
          <Store className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-semibold text-slate-900 truncate">{merchant.business_name}</p>
            {isPreferred && (
              <Star className="w-4 h-4 text-amber-500 fill-amber-500 flex-shrink-0" />
            )}
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            {merchant.location && (
              <>
                <MapPin className="w-3 h-3" />
                <span className="truncate">{merchant.location}</span>
              </>
            )}
          </div>
          <div className="flex gap-2 mt-1">
            {merchant.delivery_available && (
              <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                🛵 Delivery
              </span>
            )}
            {merchant.whatsapp_available && (
              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                WhatsApp
              </span>
            )}
          </div>
        </div>
        {isSelected && (
          <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
        )}
      </div>
    </button>
  );
}

export default function BuyForFamily({ 
  isOpen, 
  onClose, 
  onSuccess, 
  preselectedBeneficiary = null,
  buyAgainData = null  // { beneficiary, merchant, amount } for Buy Again flow
}) {
  const { token, user } = useAuth();
  const { language } = useLanguage();
  
  // Current step (0-indexed)
  const [step, setStep] = useState(0);
  
  // Buy Again mode detection
  const isBuyAgain = !!buyAgainData;
  
  // Form data
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(preselectedBeneficiary);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [amount, setAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  
  // Data
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [priceQuote, setPriceQuote] = useState(null);
  const [authorizationResult, setAuthorizationResult] = useState(null);
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Translations
  const t = {
    en: {
      title: isBuyAgain ? 'Buy Again' : 'Buy for Family',
      step1Title: 'Who are you buying for?',
      step2Title: 'Where will they shop?',
      step3Title: 'How much?',
      step4Title: 'Review & Confirm',
      addFamily: '+ Add Family Member',
      next: 'Next',
      back: 'Back',
      confirm: 'Confirm Purchase Access',
      confirming: 'Processing...',
      purchaseAmount: 'Purchases',
      serviceFee: 'PeCash Service Fee',
      total: 'Total',
      available: 'Available for purchases',
      at: 'at',
      for: 'For',
      success: 'Ready!',
      successMessage: 'Purchase access created',
      notifyBeneficiary: 'Notify',
      viewDetails: 'View Details',
      buyAgain: 'Buy for Someone Else',
      done: 'Done',
      customAmount: 'Custom amount',
      enterAmount: 'Enter amount',
      recentBeneficiary: 'Recent',
      preferredMerchant: 'Preferred',
      pilotNote: 'SIMULATED (PILOT MODE)',
    },
    es: {
      title: 'Comprar para Familia',
      step1Title: '¿Para quién compras?',
      step2Title: '¿Dónde comprará?',
      step3Title: '¿Cuánto?',
      step4Title: 'Revisar y Confirmar',
      addFamily: '+ Agregar Familiar',
      next: 'Siguiente',
      back: 'Atrás',
      confirm: 'Confirmar Compra',
      confirming: 'Procesando...',
      purchaseAmount: 'Compras',
      serviceFee: 'Tarifa PeCash',
      total: 'Total',
      available: 'Disponible para compras',
      at: 'en',
      for: 'Para',
      success: '¡Listo!',
      successMessage: 'Acceso a compras creado',
      notifyBeneficiary: 'Notificar',
      viewDetails: 'Ver Detalles',
      buyAgain: 'Comprar para Otro',
      done: 'Listo',
      customAmount: 'Monto personalizado',
      enterAmount: 'Ingrese monto',
      recentBeneficiary: 'Reciente',
      preferredMerchant: 'Preferido',
      pilotNote: 'SIMULADO (MODO PILOTO)',
    }
  };
  const text = t[language] || t.en;

  // Fetch beneficiaries
  const fetchBeneficiaries = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/beneficiaries`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBeneficiaries(data.beneficiaries || []);
      }
    } catch (error) {
      console.error('Failed to fetch beneficiaries:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch merchants
  const fetchMerchants = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/merchants/active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMerchants(data.merchants || data || []);
      }
    } catch (error) {
      console.error('Failed to fetch merchants:', error);
    }
  }, [token]);

  // Fetch price quote
  const fetchPriceQuote = useCallback(async (amountUsd) => {
    if (!amountUsd || amountUsd < 10) {
      setPriceQuote(null);
      return;
    }
    setPriceLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/pricing/quote?amount_usd=${amountUsd}${selectedMerchant ? `&merchant_id=${selectedMerchant.id}` : ''}`
      );
      if (response.ok) {
        const data = await response.json();
        setPriceQuote(data);
      }
    } catch (error) {
      console.error('Failed to fetch price quote:', error);
    } finally {
      setPriceLoading(false);
    }
  }, [selectedMerchant]);

  // Initialize data
  useEffect(() => {
    if (isOpen) {
      fetchBeneficiaries();
      fetchMerchants();
      
      // Handle Buy Again flow - pre-fill from recent authorization
      if (buyAgainData) {
        // Pre-select beneficiary
        if (buyAgainData.beneficiary) {
          setSelectedBeneficiary(buyAgainData.beneficiary);
        }
        // Pre-select merchant
        if (buyAgainData.merchant) {
          setSelectedMerchant(buyAgainData.merchant);
        }
        // Pre-fill amount (optional)
        if (buyAgainData.amount && buyAgainData.amount >= 10 && buyAgainData.amount <= 200) {
          setAmount(buyAgainData.amount.toString());
        }
        // If both beneficiary and merchant are pre-filled, skip to amount step
        if (buyAgainData.beneficiary && buyAgainData.merchant) {
          setStep(2); // Skip to amount selection
        } else if (buyAgainData.beneficiary) {
          setStep(1); // Skip to merchant selection
        }
      } else {
        // Reset state for fresh flow
        if (!preselectedBeneficiary) {
          setSelectedBeneficiary(null);
        }
        setSelectedMerchant(null);
        setAmount('');
        setCustomAmount('');
        setPriceQuote(null);
        setAuthorizationResult(null);
        setStep(0);
      }
    }
  }, [isOpen, fetchBeneficiaries, fetchMerchants, preselectedBeneficiary, buyAgainData]);

  // Update price quote when amount changes
  useEffect(() => {
    const amountValue = amount === 'custom' ? parseFloat(customAmount) : parseFloat(amount);
    if (amountValue > 0) {
      fetchPriceQuote(amountValue);
    }
  }, [amount, customAmount, fetchPriceQuote]);

  // Handle amount selection
  const handleAmountSelect = (value) => {
    setAmount(value.toString());
    setCustomAmount('');
  };

  // Handle custom amount
  const handleCustomAmount = (value) => {
    setCustomAmount(value);
    setAmount('custom');
  };

  // Submit purchase
  const handleSubmit = async () => {
    if (!selectedBeneficiary || !selectedMerchant || !priceQuote) return;
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/authorizations/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          merchant_id: selectedMerchant.id,
          recipient_name: selectedBeneficiary.name,
          recipient_email: selectedBeneficiary.email,
          recipient_phone: selectedBeneficiary.phone,
          usd_amount: priceQuote.purchase_amount_usd,
          description: `Purchase access for ${selectedBeneficiary.name} at ${selectedMerchant.business_name}`
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAuthorizationResult(data);
        setStep(4); // Success step
        toast.success(text.successMessage);
        if (onSuccess) onSuccess(data);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create purchase access');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  // Close and reset
  const handleClose = () => {
    setStep(0);
    setSelectedBeneficiary(null);
    setSelectedMerchant(null);
    setAmount('');
    setCustomAmount('');
    setPriceQuote(null);
    setAuthorizationResult(null);
    onClose();
  };

  // Navigation
  const canGoNext = () => {
    switch (step) {
      case 0: return !!selectedBeneficiary;
      case 1: return !!selectedMerchant;
      case 2: return priceQuote && priceQuote.purchase_amount_usd >= 10;
      case 3: return true;
      default: return false;
    }
  };

  const goNext = () => {
    if (canGoNext() && step < 3) setStep(step + 1);
  };

  const goBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const selectedAmount = amount === 'custom' ? parseFloat(customAmount) : parseFloat(amount);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden max-h-[90vh]">
        <DialogTitle className="sr-only">{text.title}</DialogTitle>
        
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-100 p-4 pb-2">
          <div className="flex items-center justify-between mb-2">
            {step > 0 && step < 4 ? (
              <button onClick={goBack} className="p-2 -ml-2 text-slate-500 hover:text-slate-700">
                <ArrowLeft className="w-5 h-5" />
              </button>
            ) : (
              <div className="w-9" />
            )}
            <h2 className="text-lg font-semibold text-slate-900">{text.title}</h2>
            <button onClick={handleClose} className="p-2 -mr-2 text-slate-400 hover:text-slate-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          {step < 4 && <StepIndicator currentStep={step} totalSteps={4} />}
        </div>

        {/* Content */}
        <div className="overflow-y-auto p-4 max-h-[60vh]">
          <AnimatePresence mode="wait">
            {/* STEP 0: Select Beneficiary */}
            {step === 0 && (
              <motion.div
                key="step-0"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <h3 className="text-xl font-bold text-slate-900">{text.step1Title}</h3>
                
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
                  </div>
                ) : beneficiaries.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No family members yet</p>
                    <Button className="mt-4" variant="outline">
                      <Plus className="w-4 h-4 mr-2" />
                      {text.addFamily}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {beneficiaries.map((b, idx) => (
                      <BeneficiaryCard
                        key={b.id}
                        beneficiary={b}
                        isSelected={selectedBeneficiary?.id === b.id}
                        onSelect={setSelectedBeneficiary}
                        isRecent={idx === 0}
                      />
                    ))}
                    <button className="w-full p-4 rounded-2xl border-2 border-dashed border-slate-200 text-slate-500 hover:border-purple-300 hover:text-purple-600 transition-colors">
                      <Plus className="w-5 h-5 mx-auto mb-1" />
                      <span className="text-sm">{text.addFamily}</span>
                    </button>
                  </div>
                )}
              </motion.div>
            )}

            {/* STEP 1: Select Merchant */}
            {step === 1 && (
              <motion.div
                key="step-1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <h3 className="text-xl font-bold text-slate-900">{text.step2Title}</h3>
                
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {merchants.map((m, idx) => (
                    <MerchantCard
                      key={m.id}
                      merchant={m}
                      isSelected={selectedMerchant?.id === m.id}
                      onSelect={setSelectedMerchant}
                      isPreferred={idx === 0}
                    />
                  ))}
                </div>
              </motion.div>
            )}

            {/* STEP 2: Select Amount */}
            {step === 2 && (
              <motion.div
                key="step-2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <h3 className="text-xl font-bold text-slate-900">{text.step3Title}</h3>
                
                {/* Quick amount buttons */}
                <div className="grid grid-cols-3 gap-3">
                  {QUICK_AMOUNTS.map((amt) => (
                    <button
                      key={amt}
                      onClick={() => handleAmountSelect(amt)}
                      className={`py-4 rounded-xl font-bold text-lg transition-all ${
                        amount === amt.toString()
                          ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/30'
                          : 'bg-slate-100 text-slate-700 hover:bg-purple-100 hover:text-purple-700'
                      }`}
                      data-testid={`amount-${amt}`}
                    >
                      ${amt}
                    </button>
                  ))}
                </div>

                {/* Custom amount */}
                <div className="mt-4">
                  <label className="text-sm text-slate-600">{text.customAmount}</label>
                  <div className="relative mt-1">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium">$</span>
                    <Input
                      type="number"
                      min="10"
                      max="200"
                      step="0.01"
                      value={customAmount}
                      onChange={(e) => handleCustomAmount(e.target.value)}
                      placeholder={text.enterAmount}
                      className="pl-8 h-14 text-lg font-semibold"
                      data-testid="buy-custom-amount-input"
                    />
                  </div>
                </div>

                {/* Price preview */}
                {priceLoading ? (
                  <div className="flex justify-center py-4" data-testid="buy-price-loading">
                    <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
                  </div>
                ) : priceQuote && (
                  <div className="bg-slate-50 rounded-xl p-4 mt-4" data-testid="buy-price-preview">
                    <div className="flex justify-between mb-2">
                      <span className="text-slate-600">{text.serviceFee}</span>
                      <span className="font-medium" data-testid="buy-service-fee">${priceQuote.service_fee?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg border-t border-slate-200 pt-2 mt-2">
                      <span>{text.total}</span>
                      <span className="text-purple-600" data-testid="buy-customer-total">${priceQuote.total_to_pay?.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* STEP 3: Review */}
            {step === 3 && priceQuote && (
              <motion.div
                key="step-3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <h3 className="text-xl font-bold text-slate-900">{text.step4Title}</h3>
                
                {/* Summary card */}
                <div className="bg-gradient-to-br from-purple-50 to-white rounded-2xl border border-purple-100 p-5">
                  {/* Beneficiary */}
                  <div className="flex items-center gap-3 pb-4 border-b border-purple-100">
                    <div className="w-12 h-12 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-lg">
                      {selectedBeneficiary?.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm text-purple-600">{text.for}</p>
                      <p className="font-bold text-lg text-slate-900">{selectedBeneficiary?.name}</p>
                    </div>
                  </div>

                  {/* Merchant */}
                  <div className="flex items-center gap-3 py-4 border-b border-purple-100">
                    <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center">
                      <Store className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{text.at}</p>
                      <p className="font-semibold text-slate-900">{selectedMerchant?.business_name}</p>
                    </div>
                  </div>

                  {/* Amount breakdown */}
                  <div className="pt-4 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-600">{text.purchaseAmount}</span>
                      <span className="font-medium">${priceQuote.purchase_amount_usd?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">{text.available}</span>
                      <span className="text-emerald-600 font-medium">
                        RD${priceQuote.purchase_amount_dop?.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">{text.serviceFee}</span>
                      <span className="font-medium">${priceQuote.service_fee?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-xl pt-3 border-t border-purple-100 mt-2">
                      <span>{text.total}</span>
                      <span className="text-purple-600">${priceQuote.total_to_pay?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Pilot mode notice */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
                  <p className="text-amber-700 text-sm font-medium">{text.pilotNote}</p>
                </div>
              </motion.div>
            )}

            {/* STEP 4: Success */}
            {step === 4 && authorizationResult && (
              <motion.div
                key="step-4"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center space-y-4"
              >
                {/* Success animation */}
                <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  >
                    <CheckCircle2 className="w-10 h-10 text-emerald-600" />
                  </motion.div>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-slate-900">{text.success}</h3>
                  <p className="text-slate-600">{text.successMessage}</p>
                </div>

                {/* Amount available */}
                <div className="bg-emerald-50 rounded-2xl p-6">
                  <p className="text-emerald-600 text-sm mb-1">{text.available}</p>
                  <p className="text-4xl font-bold text-emerald-700">
                    RD${authorizationResult.authorization?.dop_authorized_amount?.toLocaleString() || priceQuote?.purchase_amount_dop?.toLocaleString()}
                  </p>
                  <p className="text-emerald-600 mt-2">
                    {text.at} {selectedMerchant?.business_name}
                  </p>
                </div>

                {/* QR Code */}
                {authorizationResult.redemption?.numeric_code && (
                  <div className="bg-white border-2 border-slate-200 rounded-xl p-4">
                    <div className="flex justify-center mb-3">
                      <QRCode 
                        value={`${window.location.origin}/redeem/${authorizationResult.redemption?.token_value || authorizationResult.redemption?.numeric_code}`}
                        size={150}
                        level="M"
                      />
                    </div>
                    <p className="font-mono text-2xl font-bold tracking-wider">
                      {authorizationResult.redemption?.numeric_code}
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(authorizationResult.redemption?.numeric_code);
                        toast.success('Copied!');
                      }}
                      className="mt-2"
                    >
                      <Copy className="w-4 h-4 mr-1" /> Copy Code
                    </Button>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <Button
                    className="flex-1 h-12 bg-purple-600 hover:bg-purple-700"
                    onClick={() => {
                      // Notify beneficiary
                      toast.success('Notification sent!');
                    }}
                  >
                    <Bell className="w-4 h-4 mr-2" />
                    {text.notifyBeneficiary}
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 h-12"
                    onClick={handleClose}
                  >
                    {text.done}
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer with navigation */}
        {step < 4 && (
          <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4">
            {step < 3 ? (
              <Button
                onClick={goNext}
                disabled={!canGoNext()}
                className="w-full h-14 bg-purple-600 hover:bg-purple-700 text-lg font-semibold rounded-xl"
                data-testid="buy-next-btn"
                aria-label={`Continue to ${step === 0 ? 'merchant selection' : step === 1 ? 'amount selection' : 'review'}`}
              >
                {text.next}
                <ArrowRight className="w-5 h-5 ml-2" aria-hidden="true" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-lg font-semibold rounded-xl"
                data-testid="buy-confirm-btn"
                aria-label={`Confirm purchase of $${selectedAmount} for ${selectedBeneficiary?.first_name || 'family member'}`}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" aria-hidden="true" />
                    {text.confirming}
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5 mr-2" aria-hidden="true" />
                    {text.confirm}
                  </>
                )}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
